import type { HovGainUpgradeMpTarget } from "@/features/hoverIdle/types";
import { countHovGainUpgradeMpTargets } from "@/features/hoverIdle/mpm/mpmHovGainUpgradeTargets";
import type { MpmState } from "@/features/hoverIdle/mpm/mpmState";
import {
  MPM_MHOV_EXECUTION_MP_COST,
  MPM_NHOV_EXECUTION_MP_COST_PER_BRANCH,
  MPM_OHOV_EXECUTION_MP_COST,
} from "@/features/hoverIdle/mpm/mpmState";
import type { HovAmplifierEntryId } from "@/features/hoverIdle/hov/hovAmplifierModel";
import type { NHovBranchId } from "@/features/hoverIdle/types";
import { getExplorationMpSlotDefinition } from "@/features/hoverIdle/exploration/explorationDefinitions";

export function isMpmHovTargetsActive(state: MpmState): boolean {
  return state.hovTargets.unlocked && state.hovTargets.enabled;
}

export function isMpmHovGainUpgradesActive(state: MpmState): boolean {
  return state.hovGainUpgrades.unlocked && state.hovGainUpgrades.enabled;
}

export function isMpmAmplifierPanelsActive(state: MpmState): boolean {
  return state.amplifierPanels.unlocked && state.amplifierPanels.enabled;
}

export function isMpmAmplifierUpgradesActive(state: MpmState): boolean {
  return state.amplifierUpgrades.unlocked && state.amplifierUpgrades.enabled;
}

export function isMpmMHovExecutionActive(state: MpmState): boolean {
  return state.mhovExecution.unlocked && state.mhovExecution.enabled;
}

export function isMpmNHovExecutionActive(state: MpmState): boolean {
  return state.nhovExecution.unlocked && state.nhovExecution.enabled;
}

export function isMpmOHovExecutionActive(state: MpmState): boolean {
  return state.ohovExecution.unlocked && state.ohovExecution.enabled;
}

export function isMpmExplorationActive(state: MpmState): boolean {
  return state.exploration.enabled;
}

export function getActiveMpmHovTargetAssignments(
  state: MpmState,
): readonly number[] {
  return isMpmHovTargetsActive(state) ? state.hovTargets.assignments : [];
}

export function getActiveMpmHovGainUpgradeAssignments(
  state: MpmState,
): readonly HovGainUpgradeMpTarget[] {
  return isMpmHovGainUpgradesActive(state)
    ? state.hovGainUpgrades.assignments
    : [];
}

export function getActiveMpmAmplifierPanelAssignments(
  state: MpmState,
): readonly HovAmplifierEntryId[] {
  return isMpmAmplifierPanelsActive(state)
    ? state.amplifierPanels.assignments
    : [];
}

export function getActiveMpmAmplifierUpgradeAssignments(
  state: MpmState,
): readonly HovAmplifierEntryId[] {
  return isMpmAmplifierUpgradesActive(state)
    ? state.amplifierUpgrades.assignments
    : [];
}

export function getActiveMpmNHovExecutionAssignments(
  state: MpmState,
): readonly NHovBranchId[] {
  return isMpmNHovExecutionActive(state) ? state.nhovExecution.assignments : [];
}

export function isMpmOHovExecutionAssignedActive(state: MpmState): boolean {
  return isMpmOHovExecutionActive(state) && state.ohovExecution.assigned;
}

export function computeAssignedMpCount(state: MpmState): number {
  return (
    getActiveMpmHovTargetAssignments(state).length +
    countHovGainUpgradeMpTargets(getActiveMpmHovGainUpgradeAssignments(state)) +
    getActiveMpmNHovExecutionAssignments(state).length *
      MPM_NHOV_EXECUTION_MP_COST_PER_BRANCH +
    (isMpmOHovExecutionAssignedActive(state) ? MPM_OHOV_EXECUTION_MP_COST : 0) +
    countAmplifierMpTargets(getActiveMpmAmplifierPanelAssignments(state)) +
    countAmplifierMpTargets(getActiveMpmAmplifierUpgradeAssignments(state)) +
    getActiveExplorationAssignedMp(state)
  );
}

export function computeAvailableMp(options: {
  readonly mpCapacity: number;
  readonly state: MpmState;
}): number {
  if (
    typeof options.mpCapacity !== "number" ||
    !Number.isInteger(options.mpCapacity) ||
    options.mpCapacity < 0
  ) {
    return 0;
  }
  return Math.max(
    0,
    options.mpCapacity - computeAssignedMpCount(options.state),
  );
}

// Counts every MP held under MPM regardless of section `unlocked` / `enabled`
// flags. Use this for capacity/occupancy surfaces (e.g. the MPM availability
// HUD): once MP is assigned, it stays occupied until the player Removes it,
// even if the owning section is later disabled. Future MP-assigning sections
// (mhov / nhov / ohov / ...) must be added here so the HUD keeps reflecting
// total MP occupancy.
export function computeMpmAssignedMpOccupancy(state: MpmState): number {
  return (
    state.hovTargets.assignments.length +
    countHovGainUpgradeMpTargets(state.hovGainUpgrades.assignments) +
    (state.mhovExecution.assigned ? MPM_MHOV_EXECUTION_MP_COST : 0) +
    state.nhovExecution.assignments.length *
      MPM_NHOV_EXECUTION_MP_COST_PER_BRANCH +
    (state.ohovExecution.assigned ? MPM_OHOV_EXECUTION_MP_COST : 0) +
    countAmplifierMpTargets(state.amplifierPanels.assignments) +
    countAmplifierMpTargets(state.amplifierUpgrades.assignments) +
    getExplorationAssignedMpOccupancy(state)
  );
}

function countAmplifierMpTargets(
  targets: readonly HovAmplifierEntryId[],
): number {
  return new Set(targets).size;
}

function getActiveExplorationAssignedMp(state: MpmState): number {
  return isMpmExplorationActive(state)
    ? getExplorationAssignedMpOccupancy(state)
    : 0;
}

function getExplorationAssignedMpOccupancy(state: MpmState): number {
  return state.exploration.assignedSlotIds.reduce<number>(
    (total, slotId) => total + getExplorationMpSlotDefinition(slotId).amount,
    0,
  );
}

export function computeMpmAvailableMpFromOccupancy(options: {
  readonly mpCapacity: number;
  readonly state: MpmState;
}): number {
  if (
    typeof options.mpCapacity !== "number" ||
    !Number.isInteger(options.mpCapacity) ||
    options.mpCapacity < 0
  ) {
    return 0;
  }
  return Math.max(
    0,
    options.mpCapacity - computeMpmAssignedMpOccupancy(options.state),
  );
}
